import { useCallback } from 'react';
import { toast } from 'sonner';
import { useNodeStore } from '../stores/nodeStore';
import { useProjectStore } from '../stores/projectStore';
import { useTaskStore } from '../stores/taskStore';
import { useDataPersistence, setPersistedGroups, getPersistedGroups } from './useDataPersistence';
import { unifiedStorage } from '../services/unifiedStorage';
import { projectApi } from '../services/projectApi';
import { isBackendAssetUrl, normalizeAssetUrl } from '../utils/assetUtils';
import { useGroupStore } from '../stores/groupStore';
import { useCanvasStore } from '../stores/canvasStore';
import { generateApi } from '../services/generateApi';
import { findStrategy, GenerationContext } from '../services/generationStrategies';
import { eventBus, CanvasEvents } from '../utils/eventBus';
import { NodeStatus, NodeType, AppNode } from '../types';

function reconcileNodes(nodes: AppNode[]): AppNode[] {
    return nodes.map((node) => {
        if (node.status !== NodeStatus.WORKING) return node;
        const d = node.data;
        if (node.type === NodeType.MEDIA_UPLOAD) {
            return { ...node, status: NodeStatus.IDLE };
        }
        // 优先检查 pendingTasks：有正在运行的任务 → 保持 WORKING
        // 这样即使节点 data 里保留着上一次的旧生成结果，也不会被误判为 SUCCESS
        const pendingTask = useTaskStore.getState().getTaskByNodeId(node.id);
        if (pendingTask && pendingTask.status === 'RUNNING') {
            return { ...node, status: NodeStatus.WORKING, data: { ...node.data, progress: '恢复任务中...' } };
        }
        // 有 taskId/pendingTaskId 但不在 pendingTasks 中 → 标记需要恢复
        // 由 recoverWorkingNodes 通过后端 API 按 project_id 批量查询运行中任务来恢复
        if (d.taskId || d.pendingTaskId) {
            return { ...node, status: NodeStatus.WORKING, data: { ...node.data, progress: '正在检查任务状态...', _needsTaskRecovery: true } };
        }
        // 无任务标记但有结果 → SUCCESS（真正已完成的节点）
        const hasResult = d.image || (d.images && d.images.length > 0) ||
            d.videoUri || (d.videoUris && d.videoUris.length > 0) ||
            d.audioUri || d.panoramaOutputUrl || d.analysis;
        if (hasResult) {
            return { ...node, status: NodeStatus.SUCCESS };
        }
        // 无任务标记也无结果 → 标记需要恢复
        return { ...node, status: NodeStatus.WORKING, data: { ...node.data, progress: '正在检查任务状态...', _needsTaskRecovery: true } };
    });
}

function reconcileAndNormalize(nodes: AppNode[]): AppNode[] {
    const normalized = nodes.map(node => {
        const normalizedData = { ...node.data };
        if (normalizedData.image && isBackendAssetUrl(normalizedData.image)) {
            normalizedData.image = normalizeAssetUrl(normalizedData.image);
        } else if (typeof normalizedData.image === 'string' && normalizedData.image.includes('\\')) {
            normalizedData.image = normalizedData.image.replace(/\\/g, '/');
        }
        if (normalizedData.images && Array.isArray(normalizedData.images)) {
            normalizedData.images = normalizedData.images.map((img: string) =>
                isBackendAssetUrl(img) ? normalizeAssetUrl(img) : (typeof img === 'string' && img.includes('\\') ? img.replace(/\\/g, '/') : img)
            );
        }
        if (normalizedData.videoUri && isBackendAssetUrl(normalizedData.videoUri)) {
            normalizedData.videoUri = normalizeAssetUrl(normalizedData.videoUri);
        } else if (typeof normalizedData.videoUri === 'string' && normalizedData.videoUri.includes('\\')) {
            normalizedData.videoUri = normalizedData.videoUri.replace(/\\/g, '/');
        }
        if (normalizedData.videoUris && Array.isArray(normalizedData.videoUris)) {
            normalizedData.videoUris = normalizedData.videoUris.map((vid: string) =>
                isBackendAssetUrl(vid) ? normalizeAssetUrl(vid) : (typeof vid === 'string' && vid.includes('\\') ? vid.replace(/\\/g, '/') : vid)
            );
        }
        if (typeof normalizedData.audioUri === 'string') {
            if (isBackendAssetUrl(normalizedData.audioUri)) {
                normalizedData.audioUri = normalizeAssetUrl(normalizedData.audioUri);
            } else if (normalizedData.audioUri.includes('\\')) {
                normalizedData.audioUri = normalizedData.audioUri.replace(/\\/g, '/');
            }
        }
        if (typeof normalizedData.panoramaOutputUrl === 'string') {
            if (isBackendAssetUrl(normalizedData.panoramaOutputUrl)) {
                normalizedData.panoramaOutputUrl = normalizeAssetUrl(normalizedData.panoramaOutputUrl);
            } else if (normalizedData.panoramaOutputUrl.includes('\\')) {
                normalizedData.panoramaOutputUrl = normalizedData.panoramaOutputUrl.replace(/\\/g, '/');
            }
        }
        return { ...node, data: normalizedData };
    });

    return reconcileNodes(normalized);
}

export async function recoverWorkingNodes(projectId: string) {
    const currentNodes = useNodeStore.getState().nodes;
    const { updateNode } = useNodeStore.getState();
    // 只处理 _needsTaskRecovery 标记的节点（刷新后需要恢复的节点）
    // 不处理刚刚发起新生成的 WORKING 节点（它们只有 pendingTaskId，后端任务可能还没创建）
    const workingNodes = currentNodes.filter(n => n.data._needsTaskRecovery);

    if (workingNodes.length === 0) return;

    // 从后端按 projectId 批量查询所有运行中任务（pending + running）
    // 后端 TaskRecord 表已通过迁移新增 node_id/project_id 字段，可按项目反查任务
    // 这样即使前端 taskId 持久化丢失，也能通过 node_id 反向匹配恢复
    let backendTasksByNodeId: Map<string, { taskId: string; request?: Record<string, any> }> = new Map();
    try {
        const projectTasks = await generateApi.getProjectTasks(projectId);
        for (const t of projectTasks.tasks) {
            if (t.nodeId && !backendTasksByNodeId.has(t.nodeId)) {
                backendTasksByNodeId.set(t.nodeId, { taskId: t.taskId, request: t.request });
            }
        }
    } catch (e) {
        console.warn('[Recovery] 查询项目运行中任务失败，回退到节点 taskId 单查模式:', e);
    }

    for (const node of workingNodes) {
        let taskId = node.data.taskId || node.data.pendingTaskId;
        let taskRequest: Record<string, any> | undefined;

        if (!taskId || taskId.startsWith('pending_')) {
            const backendTask = backendTasksByNodeId.get(node.id);
            if (backendTask) {
                taskId = backendTask.taskId;
                taskRequest = backendTask.request;
            }
        }

        if (!taskId || taskId.startsWith('pending_')) {
            const d = node.data;
            const hasResult = d.image || (d.images && d.images.length > 0) ||
                d.videoUri || (d.videoUris && d.videoUris.length > 0) ||
                d.audioUri || d.panoramaOutputUrl || d.analysis;
            if (hasResult) {
                updateNode(node.id, { progress: undefined, _needsTaskRecovery: false, taskId: undefined, pendingTaskId: undefined }, undefined, undefined, NodeStatus.SUCCESS);
            } else {
                updateNode(node.id, { error: '任务因页面刷新中断', progress: undefined, _needsTaskRecovery: false, taskId: undefined, pendingTaskId: undefined }, undefined, undefined, NodeStatus.IDLE);
            }
            continue;
        }

        const pendingTask = useTaskStore.getState().getTaskByNodeId(node.id);
        if (pendingTask && pendingTask.status === 'RUNNING' && pendingTask.taskId === taskId) continue;

        try {
            const data = await generateApi.getTaskStatus(taskId);

            if (data.status === 'success' && data.result) {
                const nodeType = node.type;
                const strategy = findStrategy(nodeType as any);
                const request: Record<string, any> = taskRequest || {
                    modelId: node.data.model,
                    prompt: node.data.prompt,
                    params: {
                        aspectRatio: node.data.aspectRatio,
                        resolution: node.data.resolution,
                        imageCount: node.data.imageCount,
                    },
                };

                if (strategy) {
                    const mockNode: AppNode = {
                        id: node.id,
                        type: nodeType as any,
                        x: 0, y: 0,
                        title: '',
                        status: NodeStatus.WORKING,
                        data: request,
                        inputs: [],
                    };

                    const rawResult = {
                        taskId,
                        status: 'success',
                        results: data.result.results,
                        error: data.result.error,
                        videoMetadata: data.result.videoMetadata,
                        analysis: data.result.analysis,
                    };

                    const ctx: GenerationContext = {
                        projectId,
                        onProgress: () => {},
                        onAssetGenerated: () => {},
                        onTaskCreated: () => {},
                    };

                    const updates = await strategy.processResult(mockNode, rawResult, ctx, request.prompt || '');
                    updateNode(node.id, { ...updates, progress: undefined, _needsTaskRecovery: false, taskId: undefined, pendingTaskId: undefined }, undefined, undefined, NodeStatus.SUCCESS);

                    if (node.id.startsWith('agent-node-')) {
                        eventBus.emit(CanvasEvents.AGENT_TASK_COMPLETED, {
                            taskId,
                            nodeId: node.id,
                            status: 'SUCCESS',
                            updates,
                        });
                    }
                } else {
                    updateNode(node.id, { progress: undefined, _needsTaskRecovery: false, taskId: undefined, pendingTaskId: undefined }, undefined, undefined, NodeStatus.SUCCESS);
                }
                useTaskStore.getState().removeTask(taskId);
            } else if (data.status === 'failed') {
                updateNode(node.id, { error: data.error || '任务失败', progress: undefined, _needsTaskRecovery: false, taskId: undefined, pendingTaskId: undefined }, undefined, undefined, NodeStatus.IDLE);
                useTaskStore.getState().removeTask(taskId);
            } else if (data.status === 'pending' || data.status === 'running') {
                // 用反查到的 request 重建 taskStore 条目（前端 request 丢失时尤为重要）
                useTaskStore.getState().addTaskWithRequest(taskId, node.id, node.type || '', taskRequest || {
                    modelId: node.data.model,
                    prompt: node.data.prompt,
                    params: {
                        aspectRatio: node.data.aspectRatio,
                        resolution: node.data.resolution,
                    },
                });
                updateNode(node.id, { taskId, _needsTaskRecovery: false, pendingTaskId: undefined, progress: '生成中...' }, undefined, undefined, NodeStatus.WORKING);
            } else {
                updateNode(node.id, { error: '未知任务状态', progress: undefined, _needsTaskRecovery: false, taskId: undefined, pendingTaskId: undefined }, undefined, undefined, NodeStatus.IDLE);
            }
        } catch (e: any) {
            console.error(`[Recovery] Failed to recover task for node ${node.id}:`, e);
            updateNode(node.id, { error: e.message || '恢复任务失败', progress: undefined, _needsTaskRecovery: false, taskId: undefined, pendingTaskId: undefined }, undefined, undefined, NodeStatus.IDLE);
        }
    }
}

export function useProjectNavigation(saveHistory: () => void) {
    const nodes = useNodeStore((s) => s.nodes);
    const connections = useNodeStore((s) => s.connections);
    const setNodes = useNodeStore((s) => s.setNodes);
    const setConnections = useNodeStore((s) => s.setConnections);
    const projects = useProjectStore((s) => s.projects);
    const currentProjectId = useProjectStore((s) => s.currentProjectId);
    const loadProjects = useProjectStore((s) => s.loadProjects);
    const createProject = useProjectStore((s) => s.createProject);
    const loadProject = useProjectStore((s) => s.loadProject);
    const saveCurrentProject = useProjectStore((s) => s.saveCurrentProject);
    const deleteProject = useProjectStore((s) => s.deleteProject);
    const renameProject = useProjectStore((s) => s.renameProject);
    const setCurrentProject = useProjectStore((s) => s.setCurrentProject);

    const getThumbnail = useCallback(() => {
        const thumbnailNode = nodes.find(n => n.data.image);
        return thumbnailNode?.data.image || '';
    }, [nodes]);

    const loadWorkflow = async (id: string, transitionTo: () => Promise<void>) => {
        // 加载会话开始：飞行期间新建的活节点（WORKING/QUEUED）在 setNodes 覆盖时受保护
        useNodeStore.getState().beginBatchLoad();
        await transitionTo();
        saveHistory();

        // 进入项目前 reload taskStore from localStorage
        // AppNew 启动时只 loadFromLocalStorage 一次，主页→项目切换不会重新加载
        // 这里显式重新加载，确保多标签页 / 主页切换时 taskStore 与 localStorage 同步
        try {
            useTaskStore.getState().loadFromLocalStorage();
        } catch (e) {
            console.warn('[loadWorkflow] Failed to reload taskStore from localStorage:', e);
        }

        let loadedFromCache = false;
        // 上次保存的画布视口：加载完成后原样还原（原位置、原缩放比例，不做 fitView）
        let cachedViewport: { x: number; y: number; zoom: number } | null = null;

        try {
            const cacheKey = 'bongai_project_cache_' + id;
            let cachedNodes: any[] = [];
            let cachedConns: any[] = [];
            let cachedGroups: any[] = [];

            try {
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                    const cachedData = JSON.parse(cached);
                    if (cachedData.nodes && cachedData.nodes.length > 0) {
                        cachedNodes = cachedData.nodes;
                        cachedConns = cachedData.connections || [];
                        cachedGroups = cachedData.groups || [];
                    }
                    if (cachedData.viewport) cachedViewport = cachedData.viewport;
                }
            } catch {}

            if (!cachedViewport) {
                try {
                    const rawCanvas = localStorage.getItem('bongai_canvas_' + id);
                    if (rawCanvas) {
                        const vp = (JSON.parse(rawCanvas) as any)?.viewport;
                        if (vp && Number.isFinite(vp.x) && Number.isFinite(vp.y) && Number.isFinite(vp.zoom) && vp.zoom > 0) {
                            cachedViewport = vp;
                        }
                    }
                } catch {}
            }

            if (cachedNodes.length === 0 || !cachedViewport) {
                try {
                    const canvasData = await unifiedStorage.getCanvas(id);
                    if (cachedNodes.length === 0 && canvasData && canvasData.nodes && canvasData.nodes.length > 0) {
                        cachedNodes = canvasData.nodes;
                        cachedConns = canvasData.edges || [];
                        cachedGroups = canvasData.groups || [];
                    }
                    if (!cachedViewport && canvasData?.viewport) cachedViewport = canvasData.viewport;
                } catch {}
            }

            // 还原视口：store 更新 + bump counter，未挂载的 FlowEditor 走 defaultViewport，
            // 已挂载的由 viewportSyncCounter effect 应用到 ReactFlow 实例
            if (cachedViewport) useCanvasStore.getState().restoreViewport(cachedViewport);

            if (cachedNodes.length > 0) {
                const reconciledCache = reconcileNodes(cachedNodes);
                setNodes(reconciledCache);
                setConnections(cachedConns);
                setPersistedGroups(cachedGroups);
                useGroupStore.getState().rebuildGroupsFromNodesData(reconciledCache);
                loadedFromCache = true;
            }

            const project = await projectApi.getProject(id);
            let loadedNodes = project.workflow_data?.nodes || [];
            let loadedConns = project.workflow_data?.connections || [];
            let loadedGroups = project.workflow_data?.groups || [];

            if (loadedNodes.length > 0) {
                const reconciledNodes = reconcileAndNormalize(loadedNodes);
                const nodeIds = new Set(reconciledNodes.map(n => n.id));
                const cleanedNodes = reconciledNodes.map(node => {
                    if (node.parentNode && !nodeIds.has(node.parentNode)) {
                        const { parentNode, extent, expandParent, ...rest } = node;
                        return rest;
                    }
                    return node;
                });

                setNodes(cleanedNodes);
                setConnections(loadedConns);
                setPersistedGroups(loadedGroups);
                setCurrentProject(project);
                useGroupStore.getState().rebuildGroupsFromNodesData(cleanedNodes);

                // 项目缓存统一走 UnifiedStorage 预算门 + 安全写：
                // 超预算载荷跳过 localStorage 层（IndexedDB 为主存储），
                // 消除裸写导致的 QuotaExceededError
                unifiedStorage.saveProjectCache(id, {
                    nodes: cleanedNodes,
                    connections: loadedConns,
                    groups: loadedGroups,
                    ...(cachedViewport ? { viewport: cachedViewport } : {}),
                });
                unifiedStorage.saveToIndexedDB('nodes', cleanedNodes);
                unifiedStorage.saveToIndexedDB('connections', loadedConns);
                unifiedStorage.saveToIndexedDB('groups', loadedGroups);
            } else if (loadedFromCache) {
                setCurrentProject(project);
            } else {
                setNodes([]);
                setConnections([]);
                setPersistedGroups([]);
                setCurrentProject(project);
            }

            recoverWorkingNodes(id);

            requestIdleCallback(() => {
                useCanvasStore.getState().rebuildSpatialIndex();
            });
        } catch (e: any) {
            const isNetworkError = e?.message?.includes('Failed to fetch') || e?.message?.includes('ERR_NETWORK') || e?.name === 'TypeError';
            if (!isNetworkError) {
                console.error('Failed to load project from backend, trying cache:', e);
            } else {
                console.warn('Network error loading project, falling back to cache');
            }

            if (!loadedFromCache) {
                try {
                    const cacheKey = 'bongai_project_cache_' + id;
                    const cached = localStorage.getItem(cacheKey);
                    if (cached) {
                        const data = JSON.parse(cached);
                        const cachedNodes = reconcileNodes(data.nodes || []);
                        setNodes(cachedNodes);
                        setConnections(data.connections || []);
                        setPersistedGroups(data.groups || []);
                        setCurrentProject({
                            id,
                            name: '离线缓存恢复',
                            workflow_data: data,
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                        });
                        useGroupStore.getState().rebuildGroupsFromNodesData(cachedNodes);
                    }
                } catch (cacheErr) {
                    console.error('Failed to load from cache:', cacheErr);
                }
            }

            recoverWorkingNodes(id);

            requestIdleCallback(() => {
                useCanvasStore.getState().rebuildSpatialIndex();
            });
        }
        finally {
            useNodeStore.getState().endBatchLoad();
        }
    };

    const handleCreateProject = async (title: string, transitionTo: () => Promise<void>) => {
        await transitionTo();
        try {
            const project = await createProject(title);
            setNodes([]);
            setConnections([]);
            setPersistedGroups([]);
            // 新建画布时重置 viewport 为默认值（zoom=1 即 100%），确保初始画布比例固定
            useCanvasStore.getState().resetViewport();
            return project;
        } catch (e) {
            console.error('Failed to create project', e);
            return null;
        }
    };

    const handleBackToProjects = async (transitionFrom: () => Promise<void>) => {
        // 保存智能体聊天状态到本地缓存
        try {
            const { useAgentStore } = await import('@/stores/agentStore');
            useAgentStore.getState().saveToLocal();
        } catch (e) {
            console.warn('Failed to save agent state on back to projects:', e);
        }

        await transitionFrom();
        try {
            if (currentProjectId) {
                const cvState = useCanvasStore.getState();
                const viewport = { x: cvState.x, y: cvState.y, zoom: cvState.zoom };
                // 项目缓存统一走 UnifiedStorage 预算门 + 安全写：
                // 超预算载荷跳过 localStorage 层（IndexedDB 为主存储），
                // 消除裸写导致的 QuotaExceededError
                void unifiedStorage.saveProjectCache(currentProjectId, {
                    nodes,
                    connections,
                    groups: getPersistedGroups(),
                    viewport,
                });
                if (nodes.length > 0) {
                    unifiedStorage.saveToIndexedDB('nodes', nodes);
                    unifiedStorage.saveToIndexedDB('connections', connections);
                    unifiedStorage.saveToIndexedDB('groups', getPersistedGroups());
                    unifiedStorage.saveCanvas(currentProjectId, {
                        nodes,
                        edges: connections,
                        groups: getPersistedGroups(),
                        viewport: { ...viewport, updatedAt: Date.now() },
                    }).catch(() => {});
                }
                try {
                    if (nodes.length > 0) {
                        await projectApi.updateProject(currentProjectId, {
                            thumbnail: getThumbnail(),
                            workflow_data: { nodes, connections, groups: getPersistedGroups() }
                        });
                    }
                } catch (saveErr) {
                    console.error('保存到后端失败，但本地缓存已保存:', saveErr);
                }
            }
            await loadProjects();
        } catch (e) {
            console.error('Failed to save project', e);
        }
    };

    const deleteWorkflow = async (id: string) => {
        try {
            const ok = await deleteProject(id);
            if (ok) {
                toast.success('画布已删除');
            } else {
                toast.error('删除失败，请重试');
            }
        } catch (e) {
            console.error('Failed to delete project', e);
            toast.error('删除失败，请重试');
        }
    };

    const renameWorkflow = async (id: string, newTitle: string, _workflows?: any[], _setWorkflows?: (fn: (prev: any[]) => any[]) => void) => {
        try {
            const ok = await renameProject(id, newTitle);
            if (ok) {
                toast.success('重命名成功');
            } else {
                toast.error('重命名失败，请重试');
            }
        } catch (e) {
            console.error('Failed to rename project', e);
            toast.error('重命名失败，请重试');
        }
    };

    return {
        loadWorkflow,
        handleCreateProject,
        handleBackToProjects,
        deleteWorkflow,
        renameWorkflow,
        getThumbnail,
    };
}
